/**
 * User Permissions Modal - Admin Interface
 *
 * Allows admins to view and update user role assignments
 * Shows role details and associated permissions
 * with confirmation step before saving changes
 */

import { useState, useEffect } from "react";
import "./UserPermissionsModal.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Shield, User, Mail, AlertTriangle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";
import { ConfirmModal } from "@/components/ConfirmModal";

interface UserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    roleId: string | null;
  };
}

interface Role {
  id: string;
  name: string;
  description: string | null;
}

export default function UserPermissionsModal({ isOpen, onClose, user }: UserPermissionsModalProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<string>(user.roleId || "none");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMainModal, setShowMainModal] = useState(true);

  // Reset selected role when user changes or modal opens
  useEffect(() => {
    setSelectedRoleId(user.roleId || "none");
    setShowMainModal(true);
  }, [user.roleId, isOpen]);

  // Fetch all available roles
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const response = await api.get("/api/roles");
      return response; // Returns { data: [...roles], success: true }
    },
    enabled: isOpen,
  });

  // Update user role mutation.
  //
  // This goes through the dedicated admin role endpoint, NOT PUT /api/user-profiles/:id.
  // A bare UPDATE of role_id changes what the user may do but leaves their existing
  // sessions alive with the old permissions; this endpoint runs roleService, which also
  // stamps session_valid_after, busts the role cache and revokes the refresh tokens.
  // role_id is readonly on the generated route now, so that shortcut no longer exists.
  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string | null }) => {
      const response = await api.put(`/api/auth/admin/users/${userId}/role`, {
        role_id: roleId,
      });
      return response;
    },
    onSuccess: () => {
      showSuccess("Permissions Updated", "User role has been updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["user-profiles", user.id] });
      setShowConfirmation(false);
      setShowMainModal(true); // Reset for next time
      onClose();
    },
    onError: (error: any) => {
      console.error("Update error:", error);
      showError("Update Failed", error.message || "Failed to update user role");
      setShowConfirmation(false);
      setShowMainModal(true); // Reopen main modal on error
    },
  });

  const handleSave = () => {
    // Close main modal and show confirmation dialog
    setShowMainModal(false);
    setShowConfirmation(true);
  };

  const handleConfirmUpdate = () => {
    updateUserRoleMutation.mutate({
      userId: user.id,
      roleId: selectedRoleId === "none" ? null : selectedRoleId,
    });
  };

  const handleCancelConfirmation = () => {
    // Close confirmation and reopen main modal
    setShowConfirmation(false);
    setShowMainModal(true);
  };

  const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "User";
  const hasChanges = selectedRoleId !== (user.roleId || "none");

  // Extract roles array from API response
  const roles: Role[] = rolesData?.data || [];

  // Find current role by matching user.roleId with roles array
  const currentRole = user.roleId ? roles.find(r => r.id === user.roleId) : null;

  const selectedRole = roles.find(r => r.id === selectedRoleId);

  return (
    <>
      <Dialog open={isOpen && showMainModal} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Manage User Permissions
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* User Information */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Name</p>
                  <p className="text-sm text-gray-900">{userName}</p>
                </div>
              </div>

              {user.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email</p>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Current Role</p>
                  <p className="text-sm text-gray-900 font-semibold">
                    {currentRole?.name || "No role assigned"}
                  </p>
                  {currentRole?.description && (
                    <p className="text-xs text-gray-600">{currentRole.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label htmlFor="role-select" className="text-sm font-medium">
                Select New Role
              </Label>
              <Select
                value={selectedRoleId}
                onValueChange={setSelectedRoleId}
                disabled={rolesLoading || updateUserRoleMutation.isPending}
              >
                <SelectTrigger id="role-select">
                  <SelectValue placeholder="Select a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-left">No Role</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id} className="text-left">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{role.name}</span>
                        {role.description && (
                          <span className="text-xs text-gray-500">{role.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Warning if removing role */}
            {selectedRoleId === "none" && user.roleId && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Removing the role will revoke all associated permissions for this user.
                </p>
              </div>
            )}

            {/* Changes indicator */}
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                  Changes pending
                </Badge>
                <span className="text-gray-600">
                  {selectedRoleId === "none"
                    ? `Will remove ${currentRole?.name}`
                    : `Will change to ${selectedRole?.name}`}
                </span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={updateUserRoleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateUserRoleMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateUserRoleMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmUpdate}
        title="Confirm Permission Change"
        message={
          selectedRoleId !== "none"
            ? `Are you sure you want to update ${userName}'s permissions to ${selectedRole?.name}?`
            : `Are you sure you want to remove ${userName}'s role and revoke all permissions?`
        }
        confirmText="Update Permissions"
        variant={selectedRoleId !== "none" ? "default" : "error"}
        isDangerous={selectedRoleId === "none"}
        className="permissions-confirm-modal"
      />
    </>
  );
}
